// Shared projects (PROTOCOL.md §10): each account's long-term exchange key, the project key wrapped to a member's
// exchange key (the escrow construction of §8.2, to a person instead of the relay), the member's re-wrap under their own
// vault key, and the safety code two people compare so a relay can't slip in a key of its own. WebCrypto only, so the
// page, the app, the Worker's tests and the CLI share it; the Apple apps do the same with CryptoKit (P256.KeyAgreement,
// HKDF<SHA256>, AES.GCM) and check themselves against test/vectors/crypto-v1.json.
import { concat, fromB64u, toB64u, toCrockford } from './b64.ts';
import { hkdf, open, random, seal, sha256, unwrapProjectKey, wrapProjectKey } from './crypto.ts';

type Bytes = Uint8Array<ArrayBuffer>;
const subtle = crypto.subtle;
const ECDH = { name: 'ECDH', namedCurve: 'P-256' } as const;

// The exchange key ---------------------------------------------------------------------------------------------------

export const EXCHANGE_INFO = 'writing-suite/exchange-key/v1';
export const exchangeAAD = (accountId: string) => `${EXCHANGE_INFO}:${accountId}`;
/** `HKDF(AVK, "writing-suite/exchange-key/v1")`: seals the exchange key's private half. */
export const exchangeSealKey = (avk: Uint8Array) => hkdf(avk, EXCHANGE_INFO);

/** An account's exchange key pair: the 65-byte uncompressed public point and the private key (in memory only). */
export interface ExchangeKey {
  pub: Bytes;
  privateKey: CryptoKey;
}

/** What the relay keeps (`PUT /api/account/exchange-key`): the public point and the private scalar sealed under the AVK. */
export interface ExchangeKeyUpload {
  publicKey: string;
  sealedPrivate: string;
}

/** The P-256 private key from its 32-byte scalar `d` and its public point (WebCrypto takes a raw scalar only as JWK). */
export async function importExchangePrivate(d: Uint8Array, pub: Uint8Array): Promise<CryptoKey> {
  if (d.length !== 32 || pub.length !== 65 || pub[0] !== 4) throw new Error('not a P-256 key pair');
  const jwk: JsonWebKey = { kty: 'EC', crv: 'P-256', d: toB64u(d), x: toB64u(pub.subarray(1, 33)), y: toB64u(pub.subarray(33, 65)), ext: false };
  return subtle.importKey('jwk', jwk, ECDH, false, ['deriveBits']);
}

/** A new exchange key: the pair (private key not extractable once made) and the 32-byte scalar to seal. */
export async function newExchangeKey(): Promise<{ key: ExchangeKey; d: Bytes }> {
  const pair = (await subtle.generateKey(ECDH, true, ['deriveBits'])) as CryptoKeyPair;
  const jwk = (await subtle.exportKey('jwk', pair.privateKey)) as JsonWebKey;
  const pub = new Uint8Array((await subtle.exportKey('raw', pair.publicKey)) as ArrayBuffer);
  const d = fromB64u(jwk.d!);
  return { key: { pub, privateKey: await importExchangePrivate(d, pub) }, d };
}

/** The private scalar sealed for the relay: `Seal(HKDF(AVK, info), d, "writing-suite/exchange-key/v1:" + account)`. */
export async function sealExchangeKey(avk: Uint8Array, accountId: string, d: Uint8Array, nonce?: Uint8Array): Promise<Bytes> {
  return seal(await exchangeSealKey(avk), d, exchangeAAD(accountId), nonce);
}

/** The exchange key from the relay's copy, opened with the AVK. Throws when it doesn't open (another vault's). */
export async function openExchangeKey(avk: Uint8Array, accountId: string, upload: ExchangeKeyUpload): Promise<ExchangeKey> {
  const pub = fromB64u(upload.publicKey);
  const d = await open(await exchangeSealKey(avk), fromB64u(upload.sealedPrivate), exchangeAAD(accountId));
  return { pub, privateKey: await importExchangePrivate(d, pub) };
}

/** A new exchange key and what to upload for it. */
export async function makeExchangeKey(avk: Uint8Array, accountId: string): Promise<{ key: ExchangeKey; upload: ExchangeKeyUpload }> {
  const { key, d } = await newExchangeKey();
  const sealed = await sealExchangeKey(avk, accountId, d);
  d.fill(0);
  return { key, upload: { publicKey: toB64u(key.pub), sealedPrivate: toB64u(sealed) } };
}

// The member wrap -------------------------------------------------------------------------------------------------------

export const MEMBER_INFO = 'writing-suite/member/v1';
export const memberAAD = (owner: string, projectId: string, member: string) => `${MEMBER_INFO}:${owner}:${projectId}:${member}`;
/** 65-byte ephemeral point ‖ nonce ‖ 32-byte key ‖ tag. */
export const MEMBER_WRAP_BYTES = 65 + 12 + 32 + 16;

async function ecdh(privateKey: CryptoKey, otherPub: Uint8Array): Promise<Bytes> {
  const other = await subtle.importKey('raw', otherPub as Bytes, ECDH, false, []);
  // The standard field is `public` (Workers' type definitions call it `$public`; the runtime takes `public`).
  const algorithm = { name: 'ECDH', public: other } as unknown as Parameters<typeof subtle.deriveBits>[0];
  return new Uint8Array(await subtle.deriveBits(algorithm, privateKey, 256));
}

/** `HKDF(ECDH x, salt = ephemeralPub ‖ memberPub, "writing-suite/member/v1")` (for the test vectors). */
export async function memberWrapKey(privateKey: CryptoKey, otherPub: Uint8Array, ephemeralPub: Uint8Array, memberPub: Uint8Array): Promise<Bytes> {
  return hkdf(await ecdh(privateKey, otherPub), MEMBER_INFO, concat(ephemeralPub, memberPub));
}

/**
 * The project key wrapped to a member's exchange key: `ephemeralPub ‖ Seal(K, projectKey, memberAAD)` (125 bytes).
 * `ephemeral` and `nonce` only for test vectors.
 */
export async function wrapForMember(
  memberPub: Uint8Array,
  owner: string,
  projectId: string,
  member: string,
  projectKey: Uint8Array,
  o: { ephemeral?: { privateKey: CryptoKey; pub: Uint8Array }; nonce?: Uint8Array } = {},
): Promise<Bytes> {
  let ephemeral = o.ephemeral;
  if (!ephemeral) {
    const pair = (await subtle.generateKey(ECDH, true, ['deriveBits'])) as CryptoKeyPair;
    ephemeral = { privateKey: pair.privateKey, pub: new Uint8Array((await subtle.exportKey('raw', pair.publicKey)) as ArrayBuffer) };
  }
  const k = await memberWrapKey(ephemeral.privateKey, memberPub, ephemeral.pub, memberPub);
  return concat(ephemeral.pub, await seal(k, projectKey, memberAAD(owner, projectId, member), o.nonce ?? random(12)));
}

/** The member's side: the project key from a member wrap, with their exchange key. Throws when it doesn't open. */
export async function unwrapForMember(key: ExchangeKey, owner: string, projectId: string, member: string, wrapped: Uint8Array): Promise<Bytes> {
  if (wrapped.length !== MEMBER_WRAP_BYTES) throw new Error('a member wrap is 125 bytes');
  const ephemeralPub = wrapped.subarray(0, 65);
  const k = await memberWrapKey(key.privateKey, ephemeralPub, ephemeralPub, key.pub);
  return open(k, wrapped.subarray(65), memberAAD(owner, projectId, member));
}

// The member's own copy ------------------------------------------------------------------------------------------------

/** The project id a member's AVK wrap names: `<owner>:<project>` (so its AAD is `…/project-key/v1:<member>:<owner>:<project>`). */
export const sharedWrapId = (owner: string, projectId: string) => `${owner}:${projectId}`;

/** A shared project's key re-wrapped under the member's own AVK (`PUT /api/shares/{owner}/{project}/key`). */
export function wrapSharedKey(avk: Uint8Array, member: string, owner: string, projectId: string, projectKey: Uint8Array, nonce?: Uint8Array) {
  return wrapProjectKey(avk, member, sharedWrapId(owner, projectId), projectKey, nonce);
}

export function unwrapSharedKey(avk: Uint8Array, member: string, owner: string, projectId: string, wrapped: Uint8Array) {
  return unwrapProjectKey(avk, member, sharedWrapId(owner, projectId), wrapped);
}

// The safety code ------------------------------------------------------------------------------------------------------

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

/**
 * The code two people read to each other: SHA-256 of their two raw public keys, sorted bytewise and concatenated; the
 * first 60 bits as 12 Crockford characters, `XXXX-XXXX-XXXX`. The same on both sides whichever order they're given in.
 */
export async function safetyCode(pubA: Uint8Array, pubB: Uint8Array): Promise<string> {
  const [first, second] = compareBytes(pubA, pubB) <= 0 ? [pubA, pubB] : [pubB, pubA];
  const code = toCrockford(await sha256(concat(first, second)), 60);
  return `${code.slice(0, 4)}-${code.slice(4, 8)}-${code.slice(8)}`;
}

/** Two safety codes as written or read out (case, separators and look-alikes don't matter). */
export function sameSafetyCode(a: string, b: string): boolean {
  const norm = (s: string) => s.toUpperCase().replace(/[-\s]/g, '').replace(/O/g, '0').replace(/[IL]/g, '1');
  return norm(a) === norm(b) && norm(a).length === 12;
}

